import { createContext, useContext } from 'react';
import type { FunctionComponent } from 'react';

import useJobNotificationHub from './useJobNotificationHub';
import type { AddJobFn } from './types';

//  The context is what we need to make available to the rest of the application.
//  Since the handling of jobs and notifications all happens here, the only thing which
//  we need to expose is the onAddJob function (this is returned by the useJobNotificationHub hook).
export const JobNotificationContext = createContext<AddJobFn>(() => {});

/**
 * Returns `addJob`, which queues a background job to be polled until it finishes or fails,
 * then shows the toast the caller supplied for that outcome.
 *
 * The notification describes why the job was queued, so write it from the caller's point of
 * view. It must be plain JSON (it is persisted to localStorage so the toast still fires after
 * a reload). Callbacks are held in memory only and do not survive a reload. A success link's
 * `href` must be an http(s) URL or a same-origin root-relative path; any other link is dropped.
 *
 * @example
 * const MyComponent = () => {
 *   const addJob = useAddJobToNotificationQueue();
 *   const exportReport = async () => {
 *     const response = await queueReportExport();
 *     addJob(response.task.jobToken, {
 *       success: {
 *         content: 'Your report is ready.',
 *         persistent: true,
 *         link: { text: 'Download', href: response.downloadUrl },
 *       },
 *       failure: { content: 'Your report failed to generate.' },
 *     }, { onFinished: refetchReports });
 *   };
 * };
 */
export const useAddJobToNotificationQueue = () => useContext<AddJobFn>(JobNotificationContext);

/**
 * The state is all managed in the `useJobNotificationHub` hook.
 */
const JobNotificationProvider: FunctionComponent = ({ children }) => {
  const onAddJob = useJobNotificationHub();
  return (
    <JobNotificationContext.Provider value={onAddJob}>
      {children}
    </JobNotificationContext.Provider>
  );
};

export default JobNotificationProvider;
